import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { botsAPI, telegramAPI } from '../services/api';
import { Bot, Plus, MessageCircle, Users } from 'lucide-react';

// Platform-specific adapters share a single bot catalog.
const PLATFORMS = [
  { key: 'max', label: 'MAX', icon: MessageCircle },
  { key: 'telegram', label: 'Telegram', icon: Bot },
];

const OWNERSHIP_LABEL = { external: 'Внешний', segmira_managed: 'Segmira' };
const PURPOSE_LABEL = { service: 'сервисный', marketing: 'маркетинг', loyalty: 'лояльность' };

function StatusBadge({ status }) {
  const map = { active: 'badge-success', connecting: 'badge-warning', disconnected: 'badge-muted', error: 'badge-danger' };
  return <span className={`badge ${map[status] || 'badge-muted'}`}>{({ active: 'Подключён', connecting: 'Подключается', disconnected: 'Отключён', error: 'Ошибка' })[status] || status}</span>;
}

function SyncStatus({ bot }) {
  const sync = bot.audience_sync;
  const state = bot.status === 'disconnected' ? 'off' : (sync?.state || 'source_required');
  const labels = { active: 'Аудитория обновляется', waiting: 'Ожидаем первый проход', stale: 'Нет свежего обновления', source_required: 'Нужно подключить источник аудитории', off: 'Синхронизация остановлена' };
  return <div style={{ fontSize: 12, color: state === 'active' ? 'var(--success)' : 'var(--text-muted)', lineHeight: 1.6 }}>
    <div>{labels[state]}</div>
    {sync?.last_success_at && <div>Проверено: {new Date(sync.last_success_at.endsWith('Z') ? sync.last_success_at : sync.last_success_at + 'Z').toLocaleString('ru-RU')}</div>}
    {sync?.source?.startsWith('bitrix:') && <div>Источник: Битрикс · проверка каждую минуту</div>}
  </div>;
}

function BotCard({ bot }) {
  const Plat = (PLATFORMS.find(p => p.key === bot.platform) || {}).icon || Bot;
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Plat size={18} />
        <strong>{bot.title}</strong>
        <StatusBadge status={bot.status} />
        <span className="badge badge-muted">{OWNERSHIP_LABEL[bot.ownership] || bot.ownership}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {bot.bot_username ? `@${bot.bot_username} · ` : ''}
        назначение: {(bot.purposes || []).map(p => PURPOSE_LABEL[p] || p).join(', ') || '—'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        <Users size={14} /> {`${bot.subscribers_total} пользователей`}
      </div>
      <SyncStatus bot={bot} />
    </div>
  );
}

export default function BotsPage() {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [platform, setPlatform] = useState('max');
  const [dialog, setDialog] = useState(null);
  const [connections, setConnections] = useState([]);
  const [title, setTitle] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [audience, setAudience] = useState({ items: [], total: 0, offset: 0 });
  const [audienceQuery, setAudienceQuery] = useState('');
  const [audienceBusy, setAudienceBusy] = useState(false);
  const showAudience = async (bot, offset = 0, q = '') => {
    setDialog({ type: 'audience', bot }); setError(''); setAudienceBusy(true);
    try { setAudience((await telegramAPI.audience(bot.connection_id, { offset, limit: 50, q })).data); }
    catch { setError('Не удалось загрузить аудиторию. Попробуйте ещё раз.'); }
    finally { setAudienceBusy(false); }
  };
  const open = (type, bot) => { setError(''); setToken(''); setTitle(''); setDialog({ type, bot }); };
  const run = async (action) => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); setToken(''); setDialog(null); setNotice('Настройки бота сохранены'); await load(); }
    catch (e) { setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Не удалось выполнить действие. Попробуйте ещё раз.'); }
    finally { setBusy(false); }
  };
  // loading | ready | off (404 — фича выключена) | error (403/5xx/сеть)
  const [state, setState] = useState('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      const { data } = await botsAPI.list();
      setBots(Array.isArray(data) ? data : (data?.items || []));
      try { setConnections((await telegramAPI.list()).data); } catch { setConnections([]); }
      setState('ready');
    } catch (e) {
      const s = e.response?.status;
      setState(s === 404 ? 'off' : 'error');
    }
  }, []);
  useEffect(() => { load(); const timer = setInterval(load, 60000); return () => clearInterval(timer); }, [load]);

  const shown = bots.filter(b => b.platform === platform);

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title"><Bot size={24} /> Боты</h1><p className="page-subtitle">Ваши боты MAX и Telegram в одном кабинете.</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => navigate('/max/test-message')}>Проверка сообщения</button>
          <button className="btn btn-ghost" onClick={() => navigate('/max/audience')}>
            <Users size={16} /> Все MAX-пользователи
          </button>
          {state === 'ready' && (
            <button className="btn btn-primary" onClick={() => open('choose')}>
              <Plus size={16} /> Добавить бота
            </button>
          )}
        </div>
      </div>

      {/* фильтр платформ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {PLATFORMS.map(p => (
          <button key={p.key} className={`btn ${platform === p.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPlatform(p.key)}>
            <p.icon size={15} /> {p.label}
            {state === 'ready' && ` (${bots.filter(b => b.platform === p.key).length})`}
          </button>
        ))}
      </div>

      {state === 'loading' && <div className="card" style={{ color: 'var(--text-muted)' }}>Загрузка…</div>}

      {state === 'off' && (
        <div className="card">
          Модуль ботов выключен на сервере (feature flag). Включение — на стороне администратора Segmira.
        </div>
      )}

      {state === 'error' && (
        <div className="card" style={{ borderColor: 'var(--danger)' }}>
          <span style={{ color: 'var(--danger)' }}>Не удалось загрузить каталог (ошибка сервера или нет доступа).</span>
          <button className="btn btn-sm" style={{ marginLeft: 12 }} onClick={load}>Повторить</button>
        </div>
      )}

      {state === 'ready' && platform === 'telegram' && shown.length === 0 && (
        <div className="card" style={{ color: 'var(--text-muted)' }}>
          <h2>Подключите первого Telegram-бота</h2><p style={{ margin: '16px 0' }}>Добавьте токен из BotFather. Можно подключить несколько ботов — например, для разных цветочных магазинов.</p><button className="btn btn-primary" onClick={() => open('connect')}>Подключить Telegram-бота</button>
        </div>
      )}

      {state === 'ready' && platform === 'max' && shown.length === 0 && (
        <div className="card">
          <p>Нет подключённых MAX-ботов.</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            «Добавить бота» → подключение через MAX Connector. Webhook действующего бота не переключается.
          </p>
        </div>
      )}

      {state === 'ready' && shown.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
          {shown.map(b => <div key={b.id}><BotCard bot={b} />{b.platform === 'telegram' && <div className="card" style={{ marginTop: 8 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>{connections.find(c => c.id === b.connection_id)?.has_external_webhook ? 'Существующий сервис бота сохранён.' : 'Токен подключён.'} {b.subscribers_total > 0 ? 'Историческая аудитория загружена.' : 'Аудитория пока не импортирована.'}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" onClick={() => { setAudienceQuery(''); setAudience({ items: [], total: 0, offset: 0 }); showAudience(b); }}>Аудитория</button>
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => run(async () => { const r = await telegramAPI.verify(b.connection_id); if (r.data.status !== 'active') throw { response: { data: { detail: r.data.last_error } } }; })}>{b.status === 'disconnected' ? 'Подключить снова' : 'Проверить'}</button>
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => open('rotate', b)}>Заменить токен</button>
                {b.status !== 'disconnected' && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => open('disconnect', b)}>Отключить</button>}
              </div></div>}</div>)}
        </div>
      )}
      {notice && <p role="status" style={{ marginTop: 16 }}>{notice}</p>}
      {error && !dialog && <p role="alert" style={{ color: 'var(--danger)' }}>{error}</p>}
      {dialog && <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.65)', display: 'grid', placeItems: 'center', padding: 20 }}>
        <section role="dialog" aria-modal="true" aria-labelledby="bot-dialog-title" className="card" style={{ width: '100%', maxWidth: dialog.type === 'audience' ? 900 : 540, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
          <h2 id="bot-dialog-title">{dialog.type === 'audience' ? `Аудитория: ${dialog.bot.title}` : dialog.type === 'choose' ? 'Добавить бота' : dialog.type === 'disconnect' ? 'Отключить бота?' : dialog.type === 'rotate' ? 'Заменить токен' : 'Подключить Telegram-бота'}</h2>
          {dialog.type === 'audience' ? <div>
            <p style={{ color: 'var(--text-muted)', margin: '16px 0' }}>Пользователи из истории бота. Импорт не подтверждает доступность чата сейчас или согласие на рекламные сообщения.</p>
            <form style={{ display: 'flex', gap: 8, marginBottom: 16 }} onSubmit={e => { e.preventDefault(); showAudience(dialog.bot, 0, audienceQuery); }}>
              <input className="form-input" aria-label="Поиск по аудитории" placeholder="Имя, username или Telegram ID" value={audienceQuery} onChange={e => setAudienceQuery(e.target.value)} />
              <button className="btn btn-ghost" disabled={audienceBusy}>Найти</button>
            </form>
            {audienceBusy ? <p role="status">Загружаем аудиторию…</p> : <>
              <p style={{ marginBottom: 12 }}>Найдено: {audience.total}</p>
              <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}><thead><tr>{['Пользователь', 'Telegram ID', 'Последнее обращение'].map(t => <th key={t} style={{ padding: 12, color: 'var(--text-muted)' }}>{t}</th>)}</tr></thead><tbody>
                {audience.items.map(u => <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}><td style={{ padding: 12 }}>{u.display_name || 'Без имени'}{u.username && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>@{u.username}</div>}</td><td style={{ padding: 12 }}>{u.telegram_user_id}</td><td style={{ padding: 12 }}>{u.last_seen_at ? new Date(u.last_seen_at + (/[Z+-]\d{0,4}$/.test(u.last_seen_at) ? '' : 'Z')).toLocaleDateString('ru-RU') : '—'}</td></tr>)}
              </tbody></table></div>
              {!audience.items.length && <p style={{ margin: '16px 0', color: 'var(--text-muted)' }}>Пользователи не найдены. Для прежней аудитории нужна выгрузка из сервиса бота.</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}><button className="btn btn-ghost btn-sm" disabled={audience.offset === 0} onClick={() => showAudience(dialog.bot, Math.max(0, audience.offset - 50), audienceQuery)}>Назад</button><button className="btn btn-ghost btn-sm" disabled={audience.offset + audience.items.length >= audience.total} onClick={() => showAudience(dialog.bot, audience.offset + 50, audienceQuery)}>Далее</button></div>
            </>}
          </div> : dialog.type === 'choose' ? <div style={{ display: 'grid', gap: 12, marginTop: 24 }}>
            <button className="btn btn-ghost" onClick={() => navigate('/max')}><MessageCircle size={20} /> MAX</button>
            <button className="btn btn-primary" onClick={() => open('connect')}><Bot size={20} /> Telegram</button>
          </div> : dialog.type === 'disconnect' ? <>
            <p style={{ margin: '20px 0', color: 'var(--text-muted)' }}>{dialog.bot.title} будет отключён от Segmira. Сам Telegram-бот продолжит работать.</p>
            <button className="btn btn-primary" disabled={busy} onClick={() => run(() => telegramAPI.disconnect(dialog.bot.connection_id))}>Отключить от Segmira</button>
          </> : <form onSubmit={e => { e.preventDefault(); run(() => dialog.type === 'rotate' ? telegramAPI.rotate(dialog.bot.connection_id, token.trim()) : telegramAPI.connect({ title: title.trim(), token: token.trim() })); }}>
            <p style={{ color: 'var(--text-muted)', margin: '16px 0 24px' }}>Токен хранится в зашифрованном виде. Действующие сценарии и webhook бота сохраняются.</p>
            {dialog.type === 'connect' && <label style={{ display: 'block', marginBottom: 20 }}>Название в Segmira<input className="form-input" style={{ marginTop: 8 }} maxLength={100} value={title} onChange={e => setTitle(e.target.value)} placeholder="Например, Цветочная на Ленина" disabled={busy} /></label>}
            <label style={{ display: 'block', marginBottom: 16 }}>Токен BotFather<input autoFocus className="form-input" style={{ marginTop: 8 }} type="password" autoComplete="new-password" required value={token} onChange={e => setToken(e.target.value)} placeholder="123456789:AA…" disabled={busy} /></label>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>После подключения можно импортировать прежнюю аудиторию из сервиса бота. Отправки Telegram — следующий этап.</p>
            <button className="btn btn-primary" disabled={busy || !token.trim()} type="submit">{busy ? 'Проверяем Telegram…' : dialog.type === 'rotate' ? 'Проверить и заменить' : 'Проверить и подключить'}</button>
          </form>}
          {error && <p role="alert" style={{ color: 'var(--danger)', marginTop: 16 }}>{error}</p>}
          <button className="btn btn-ghost" style={{ marginTop: 16 }} disabled={busy} onClick={() => { setToken(''); setDialog(null); setError(''); }}>Закрыть</button>
        </section>
      </div>}
    </div>
  );
}
